import { http } from "@/utils/http";
import type { 
  UserUniversityResponse, 
  SetUniversityRequest, 
  ApiResponse,
  UserPreview,
  UserProfileDetail,
  UserReviewsResponse
} from "@/types";

export const usersApi = {
  /**
   * Set user's selected university
   */
  async setUserUniversity(universityId: number): Promise<ApiResponse<string>> {
    const response = await http.put<ApiResponse<string>>(
      "/api/v1/users/me/university",
      { universityId } as SetUniversityRequest
    );
    return response.data;
  },

  /**
   * Get user's selected university
   */
  async getUserUniversity(): Promise<UserUniversityResponse | null> {
    try {
      const response = await http.get<UserUniversityResponse>(
        "/api/v1/users/me/university"
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        // User hasn't selected a university yet
        return null;
      }
      throw error;
    }
  },

  /**
   * Clear user's selected university
   */
  async clearUserUniversity(): Promise<ApiResponse<string>> {
    const response = await http.delete<ApiResponse<string>>(
      "/api/v1/users/me/university"
    );
    return response.data;
  },

  /**
   * Get user preview info for hover card
   */
  async getUserPreview(userId: number): Promise<UserPreview> {
    const response = await http.get<UserPreview>(
      `/api/v1/users/${userId}/preview`
    );
    return response.data;
  },

  /**
   * Get detailed user profile
   */
  async getUserProfile(userId: number): Promise<UserProfileDetail> {
    const response = await http.get<UserProfileDetail>(
      `/api/v1/users/${userId}/profile`
    );
    return response.data;
  },

  /**
   * Get user's reviews
   */
  async getUserReviews(userId: number, page = 1, size = 10): Promise<UserReviewsResponse> {
    const response = await http.get<UserReviewsResponse>(
      `/api/v1/users/${userId}/reviews`,
      { params: { page, size } }
    );
    return response.data;
  },

  /**
   * Update user's bio
   */
  async updateUserBio(bio: string): Promise<ApiResponse<string>> {
    const response = await http.put<ApiResponse<string>>(
      "/api/v1/users/me/bio",
      { bio }
    );
    return response.data;
  },
};
